// Quest log (toggled with M) — a WoW-style list: every quest is a row with its title + progress
// summary; clicking a row expands it to show the flavour description and each objective's live
// count ("Wild hogs slain: 2/5"). Completed quests sink to the bottom, greyed with a ✓. Data
// arrives via MessageType.QuestLog (world3d calls setQuestLog); the panel re-renders live.

import { QUESTS, questById, type QuestProgressEntry } from "@ghostland/shared";
import { injectTheme } from "./theme.js";

const STYLE_ID = "ghostland-questlog-style";
const OVERLAY_ID = "gl-questlog";
const CSS = `
#${OVERLAY_ID} {
  position: fixed; inset: 0; z-index: 30; display: none; align-items: center; justify-content: center;
  background: rgba(4,0,6,.62); font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; color: var(--gl-text);
}
#${OVERLAY_ID}.open { display: flex; }
#${OVERLAY_ID} .gl-ql-card {
  width: 420px; max-height: 74vh; display: flex; flex-direction: column; overflow: hidden;
  background: var(--gl-panel); border: 1px solid var(--gl-border); border-radius: 12px;
  box-shadow: 0 16px 50px rgba(0,0,0,.7);
}
#${OVERLAY_ID} .gl-ql-head { display: flex; align-items: center; justify-content: space-between; padding: 16px 18px 12px; }
#${OVERLAY_ID} .gl-ql-head h1 { margin: 0; font-size: 16px; letter-spacing: .16em; font-family: 'Silkscreen', ui-monospace, monospace; }
#${OVERLAY_ID} .gl-ql-x { cursor: pointer; color: var(--gl-muted); font-size: 16px; line-height: 1; padding: 2px 6px; }
#${OVERLAY_ID} .gl-ql-x:hover { color: var(--gl-text); }
#${OVERLAY_ID} .gl-ql-body { overflow-y: auto; padding: 0 14px 16px; }
#${OVERLAY_ID} .gl-ql-quest { margin-bottom: 8px; border: 1px solid var(--gl-border); border-radius: 9px; background: var(--gl-panel-2); overflow: hidden; }
#${OVERLAY_ID} .gl-ql-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 10px 12px; cursor: pointer; user-select: none; }
#${OVERLAY_ID} .gl-ql-row:hover { background: rgba(255,255,255,.04); }
#${OVERLAY_ID} .gl-ql-title { font-size: 13px; font-weight: 700; letter-spacing: .03em; color: #ffd23f; }
#${OVERLAY_ID} .gl-ql-quest.done .gl-ql-title { color: var(--gl-muted); }
#${OVERLAY_ID} .gl-ql-sum { font-size: 11px; color: var(--gl-muted); white-space: nowrap; }
#${OVERLAY_ID} .gl-ql-sum .ok { color: #8de89a; }
#${OVERLAY_ID} .gl-ql-caret { color: var(--gl-muted); font-size: 10px; margin-right: 8px; }
#${OVERLAY_ID} .gl-ql-detail { display: none; padding: 2px 14px 12px; border-top: 1px solid var(--gl-border); }
#${OVERLAY_ID} .gl-ql-quest.expanded .gl-ql-detail { display: block; }
#${OVERLAY_ID} .gl-ql-desc { font-size: 12px; line-height: 1.65; color: var(--gl-muted); margin: 10px 0 10px; }
#${OVERLAY_ID} .gl-ql-objhead { font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--gl-muted); margin: 0 0 6px; }
#${OVERLAY_ID} .gl-ql-obj { display: flex; justify-content: space-between; gap: 8px; font-size: 12px; padding: 3px 0; }
#${OVERLAY_ID} .gl-ql-obj .n { color: var(--gl-text); }
#${OVERLAY_ID} .gl-ql-obj.met .n, #${OVERLAY_ID} .gl-ql-obj.met .c { color: #8de89a; }
#${OVERLAY_ID} .gl-ql-obj .c { color: var(--gl-muted); font-weight: 700; }
#${OVERLAY_ID} .gl-ql-reward { font-size: 11px; color: #e7c66a; margin-top: 10px; }
#${OVERLAY_ID} .gl-ql-empty { text-align: center; font-size: 12px; color: var(--gl-muted); padding: 20px 0 26px; }
`;

let overlay: HTMLDivElement | null = null;
let entries: QuestProgressEntry[] = [];
const expanded = new Set<string>(); // questIds the player has expanded (kept across re-renders)

function ensure(): void {
  if (overlay) return;
  injectTheme();
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;
  overlay.innerHTML = `<div class="gl-ql-card"><div class="gl-ql-head"><h1>QUESTS</h1><div class="gl-ql-x">✕</div></div><div class="gl-ql-body"></div></div>`;
  document.body.appendChild(overlay);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeQuestLog(); });
  overlay.querySelector(".gl-ql-x")!.addEventListener("click", () => closeQuestLog());
}

function render(): void {
  if (!overlay) return;
  const body = overlay.querySelector(".gl-ql-body") as HTMLDivElement;
  const esc = (s: string): string => s.replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]!));
  // Only IN-PROGRESS quests show: unaccepted quests aren't in the log, completed ones are hidden
  // (they stay in `entries` so givers never re-offer them).
  const ordered = entries.filter((e) => !e.completed);
  overlay.querySelector("h1")!.textContent = `QUESTS · ${ordered.length} ACTIVE`;
  if (!ordered.length) { body.innerHTML = `<div class="gl-ql-empty">No quest yet</div>`; return; }
  body.innerHTML = ordered.map((e) => {
    const def = questById(e.questId);
    if (!def) return "";
    const isOpen = expanded.has(e.questId);
    const objs = def.objectives.map((o, i) => {
      const p = Math.min(o.target, e.progress[i] ?? 0);
      return `<div class="gl-ql-obj${p >= o.target ? " met" : ""}"><span class="n">${esc(o.text)}</span><span class="c">${p}/${o.target}</span></div>`;
    }).join("");
    // Row summary: first objective's count (single-objective quests read exactly like WoW's tracker).
    const p0 = Math.min(def.objectives[0].target, e.progress[0] ?? 0);
    const sum = e.completed
      ? `<span class="ok">✓ Complete</span>`
      : `${p0}/${def.objectives[0].target}`;
    return `<div class="gl-ql-quest${isOpen ? " expanded" : ""}${e.completed ? " done" : ""}" data-q="${def.id}">
        <div class="gl-ql-row"><span><span class="gl-ql-caret">${isOpen ? "▼" : "▶"}</span><span class="gl-ql-title">${esc(def.title)}</span></span><span class="gl-ql-sum">${sum}</span></div>
        <div class="gl-ql-detail">
          <p class="gl-ql-desc">${esc(def.description)}</p>
          <div class="gl-ql-objhead">Objectives</div>${objs}
          <div class="gl-ql-reward">Reward: $${def.rewardCash.toLocaleString()}</div>
        </div>
      </div>`;
  }).join("");
  body.querySelectorAll<HTMLElement>(".gl-ql-row").forEach((row) => {
    row.onclick = () => {
      const id = (row.parentElement as HTMLElement).dataset.q!;
      if (expanded.has(id)) expanded.delete(id); else expanded.add(id);
      render();
    };
  });
}

/** Feed the synced quest log (from MessageType.QuestLog); re-renders if the panel is open. */
export function setQuestLog(next: QuestProgressEntry[]): void {
  entries = next.map((e) => ({ questId: e.questId, progress: [...e.progress], completed: e.completed }));
  if (overlay?.classList.contains("open")) render();
}

/** The current synced quest log (read-only) — e.g. for the NPC "!" quest-offer markers. */
export function questEntries(): readonly QuestProgressEntry[] { return entries; }

export function openQuestLog(): void {
  ensure();
  // First open: expand the first ACTIVE quest so the panel never opens as a wall of closed rows.
  if (!expanded.size) {
    const first = entries.find((e) => !e.completed) ?? entries[0];
    if (first && QUESTS.some((q) => q.id === first.questId)) expanded.add(first.questId);
  }
  render();
  overlay!.classList.add("open");
}
export function closeQuestLog(): void { overlay?.classList.remove("open"); }
export function isQuestLogOpen(): boolean { return !!overlay?.classList.contains("open"); }
export function toggleQuestLog(): void { if (isQuestLogOpen()) closeQuestLog(); else openQuestLog(); }
